import {
  PROVIDER_ANNOUNCEMENT_BANNER_COLORS,
  PROVIDER_ANNOUNCEMENT_BANNER_CONFIG_ID,
  PROVIDER_ANNOUNCEMENT_BANNER_ICONS,
  PROVIDER_ANNOUNCEMENT_BANNER_MAX_TEXT_LENGTH,
  PROVIDER_ANNOUNCEMENT_BANNER_MAX_TITLE_LENGTH,
  PROVIDER_ANNOUNCEMENT_BANNER_PRESETS,
  getProviderAnnouncementBannerRuleBaseId,
  getProviderAnnouncementBannerRuleValue,
  isProviderAnnouncementBannerValueVisible,
  providerAnnouncementBannerSchema,
  type ProviderAnnouncementBanner,
  type ProviderAnnouncementBannerConfigResponse,
  type ProviderAnnouncementBannerRule,
} from '@dev-console/provider-announcement-banner';
import { Pod } from '@shared/pods/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { BannerPreview } from '@/components/provider-banner/banner-preview';
import { ConfirmBannerDialog } from '@/components/provider-banner/confirm-banner-dialog';
import { ErrorBanner, LoadingCard } from '@/components/ui/state-views';
import { getProviderAnnouncementBanner, updateProviderAnnouncementBanner } from '@/config/api-helpers';
import { useAuth } from '@/lib/auth-context';

export const meta = {
  label: 'Provider Banner',
  order: 8,
};

const INCIDENT_LEAD_GROUP = 'SG-Eng Incident Lead';
const PROVIDER_BANNER_QUERY_KEY = ['provider-announcement-banner'];

// Mirrors the server controller's permission check; the server remains the enforcement point.
function canUpdateProviderBanner(user: { pods?: string[]; groups?: string[] } | null): boolean {
  if (user == null) return false;
  const pods = user.pods ?? [];
  return (
    pods.includes(Pod.PROVIDER) || pods.includes(Pod.PLATFORM) || (user.groups ?? []).includes(INCIDENT_LEAD_GROUP)
  );
}

type BannerDraft = {
  title: string;
  text: string;
  color: string;
  icon: string;
};

type PendingChange = { rule: ProviderAnnouncementBannerRule; banner: ProviderAnnouncementBanner | null };

function emptyDraft(): BannerDraft {
  return { title: '', text: '', color: 'error', icon: PROVIDER_ANNOUNCEMENT_BANNER_ICONS[0] };
}

// Opens on the development rule so a first click never lands on a production rule.
function pickDefaultRule(rules: ProviderAnnouncementBannerRule[]): ProviderAnnouncementBannerRule | undefined {
  return rules.find((rule) => rule.environments?.includes('development')) ?? rules[0];
}

function toDraft(value: { [Key in keyof ProviderAnnouncementBanner]?: unknown }): BannerDraft {
  const fallback = emptyDraft();
  const color = PROVIDER_ANNOUNCEMENT_BANNER_COLORS.find((option) => option === value.color);
  const icon = PROVIDER_ANNOUNCEMENT_BANNER_ICONS.find((option) => option === value.icon);
  return {
    title: typeof value.title === 'string' ? value.title : '',
    text: typeof value.text === 'string' ? value.text : '',
    color: color ?? fallback.color,
    icon: icon ?? fallback.icon,
  };
}

function formatEnvironments(rule: ProviderAnnouncementBannerRule): string {
  return rule.environments == null || rule.environments.length === 0
    ? 'all environments'
    : rule.environments.join(', ');
}

function SegmentButton({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type='button'
      aria-pressed={active}
      onClick={onClick}
      className={`h-8 rounded px-3 text-sm transition-colors ${
        active
          ? 'bg-neutral-900 text-white'
          : 'border border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400'
      }`}
    >
      {label}
    </button>
  );
}

function LiveStatus({
  config,
  rule,
  canUpdate,
  onEdit,
  onTurnOff,
}: {
  config: ProviderAnnouncementBannerConfigResponse;
  rule: ProviderAnnouncementBannerRule;
  canUpdate: boolean;
  onEdit: (value: Record<string, unknown>) => void;
  onTurnOff: () => void;
}) {
  // A rule at 0% sends everyone to the default value, but keeps its JSON for reuse.
  const isOff = rule.passPercentage === 0;
  const liveValue = getProviderAnnouncementBannerRuleValue(rule);
  const isLive = !isOff && isProviderAnnouncementBannerValueVisible(liveValue);
  const isSplit = !isOff && liveValue === undefined;
  const canTurnOff =
    isLive ||
    (isSplit &&
      rule.variants.some(
        (variant) => variant.passPercentage > 0 && isProviderAnnouncementBannerValueVisible(variant.value)
      ));
  const savedValue = isOff && isProviderAnnouncementBannerValueVisible(liveValue) ? liveValue : null;

  return (
    <div className='space-y-4 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <h3 className='font-mediumbold text-base text-neutral-900'>Live now</h3>
          <span
            className={`rounded px-2 py-0.5 text-xs font-medium ${
              isLive ? 'bg-red-100 text-red-800' : 'bg-neutral-100 text-neutral-600'
            }`}
          >
            {isSplit ? 'Split across variants' : isLive ? 'Banner showing' : 'No banner'}
          </span>
        </div>
        {canTurnOff && (
          <button
            type='button'
            disabled={!canUpdate}
            onClick={onTurnOff}
            className='h-8 rounded-md bg-red-600 px-3 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-500'
          >
            Turn off banner
          </button>
        )}
      </div>

      {!config.isEnabled && (
        <div className='rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
          This dynamic config is disabled in Statsig, so providers get the default value regardless of this rule.
        </div>
      )}
      {!isOff && rule.passPercentage < 100 && (
        <div className='rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
          This rule only passes {rule.passPercentage}% of matching providers. Showing a banner raises it to 100%.
        </div>
      )}

      {isLive && liveValue != null && <BannerPreview value={liveValue} />}

      {savedValue != null && (
        <div className='space-y-2'>
          <div className='flex items-center justify-between gap-2'>
            <span className='text-sm text-neutral-500'>Last banner on this rule (rule is at 0%)</span>
            <button
              type='button'
              onClick={() => onEdit(savedValue)}
              className='h-7 rounded-md border border-neutral-200 bg-white px-2 text-xs text-neutral-700 hover:border-neutral-400'
            >
              Use copy
            </button>
          </div>
          <div className='opacity-60'>
            <BannerPreview value={savedValue} />
          </div>
        </div>
      )}

      {rule.variants.length > 0 && (
        <table className='min-w-full divide-y divide-neutral-100 text-left text-sm'>
          <thead className='text-neutral-500'>
            <tr>
              <th className='py-2 pr-3 font-medium'>Statsig variant</th>
              <th className='py-2 pr-3 font-medium'>Split</th>
              <th className='py-2 pr-3 font-medium'>Text</th>
              <th className='py-2 font-medium' />
            </tr>
          </thead>
          <tbody className='divide-y divide-neutral-100'>
            {rule.variants.map((variant) => (
              <tr key={variant.id ?? variant.name} className='align-top'>
                <td className='py-2 pr-3 text-neutral-800'>{variant.name}</td>
                <td className='py-2 pr-3 text-neutral-700'>{variant.passPercentage}%</td>
                <td className='max-w-[480px] py-2 pr-3 text-neutral-600'>
                  {typeof variant.value?.text === 'string' && variant.value.text !== '' ? (
                    variant.value.text
                  ) : (
                    <span className='text-neutral-400'>No banner</span>
                  )}
                </td>
                <td className='py-2 text-right'>
                  {isProviderAnnouncementBannerValueVisible(variant.value) && variant.value != null && (
                    <button
                      type='button'
                      onClick={() => onEdit(variant.value ?? {})}
                      className='h-7 rounded-md border border-neutral-200 bg-white px-2 text-xs text-neutral-700 hover:border-neutral-400'
                    >
                      Use copy
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className='text-xs text-neutral-500'>
        Rule <span className='font-medium text-neutral-700'>{rule.name}</span> · {formatEnvironments(rule)}
        {config.lastModifiedTime != null && (
          <>
            {' '}
            · Last modified {new Date(config.lastModifiedTime).toLocaleString()}
            {config.lastModifierName != null && ` by ${config.lastModifierName}`}
          </>
        )}
      </div>
    </div>
  );
}

function BannerComposer({
  draft,
  canUpdate,
  onChange,
  onSubmit,
}: {
  draft: BannerDraft;
  canUpdate: boolean;
  onChange: (draft: BannerDraft) => void;
  onSubmit: (banner: ProviderAnnouncementBanner) => void;
}) {
  const parsed = providerAnnouncementBannerSchema.safeParse(draft);

  return (
    <div className='space-y-4 rounded-lg border border-neutral-200 bg-white p-4 shadow-sm'>
      <h3 className='font-mediumbold text-base text-neutral-900'>New banner</h3>

      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-sm text-neutral-500'>Examples</span>
        {PROVIDER_ANNOUNCEMENT_BANNER_PRESETS.map((preset) => (
          <button
            key={preset.label}
            type='button'
            onClick={() => onChange(toDraft(preset.banner))}
            className='h-8 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-700 hover:border-neutral-400'
          >
            {preset.label}
          </button>
        ))}
      </div>

      <label className='block space-y-1'>
        <span className='text-sm font-medium text-neutral-700'>Title (optional)</span>
        <input
          type='text'
          value={draft.title}
          maxLength={PROVIDER_ANNOUNCEMENT_BANNER_MAX_TITLE_LENGTH}
          onChange={(event) => onChange({ ...draft, title: event.target.value })}
          className='h-9 w-full rounded-md border border-neutral-200 px-3 text-sm focus:border-neutral-400 focus:outline-none'
        />
      </label>

      <label className='block space-y-1'>
        <span className='flex justify-between text-sm font-medium text-neutral-700'>
          Message
          <span className='font-normal text-neutral-400'>
            {draft.text.length}/{PROVIDER_ANNOUNCEMENT_BANNER_MAX_TEXT_LENGTH}
          </span>
        </span>
        <textarea
          value={draft.text}
          maxLength={PROVIDER_ANNOUNCEMENT_BANNER_MAX_TEXT_LENGTH}
          onChange={(event) => onChange({ ...draft, text: event.target.value })}
          rows={3}
          className='w-full rounded-md border border-neutral-200 p-3 text-sm focus:border-neutral-400 focus:outline-none'
        />
      </label>

      <div className='flex flex-wrap gap-6'>
        <div className='space-y-1'>
          <span className='text-sm font-medium text-neutral-700'>Color</span>
          <div className='flex flex-wrap gap-2'>
            {PROVIDER_ANNOUNCEMENT_BANNER_COLORS.map((color) => (
              <SegmentButton
                key={color}
                active={draft.color === color}
                label={color}
                onClick={() => onChange({ ...draft, color })}
              />
            ))}
          </div>
        </div>
        <label className='space-y-1'>
          <span className='block text-sm font-medium text-neutral-700'>Icon</span>
          <select
            value={draft.icon}
            onChange={(event) =>
              onChange({
                ...draft,
                icon:
                  PROVIDER_ANNOUNCEMENT_BANNER_ICONS.find((icon) => icon === event.target.value) ?? emptyDraft().icon,
              })
            }
            className='h-8 rounded-md border border-neutral-200 bg-white px-2 text-sm'
          >
            {PROVIDER_ANNOUNCEMENT_BANNER_ICONS.map((icon) => (
              <option key={icon} value={icon}>
                {icon}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className='space-y-1'>
        <span className='text-sm font-medium text-neutral-700'>Preview</span>
        <BannerPreview value={draft} />
      </div>

      <div className='flex flex-wrap items-center justify-end gap-3'>
        {!canUpdate && (
          <span className='text-sm text-neutral-500'>
            Requires the Provider pod, Platform pod, or SG-Eng Incident Lead.
          </span>
        )}
        <button
          type='button'
          onClick={() => onChange(emptyDraft())}
          className='h-9 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-700 hover:border-neutral-400'
        >
          Clear form
        </button>
        <button
          type='button'
          disabled={!canUpdate || !parsed.success}
          onClick={() => parsed.success && onSubmit(parsed.data)}
          className='h-9 rounded-md bg-neutral-900 px-4 text-sm font-medium text-white hover:bg-neutral-700 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-500'
        >
          Show banner
        </button>
      </div>
    </div>
  );
}

export function ProviderBannerPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canUpdate = canUpdateProviderBanner(user);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [draft, setDraft] = useState<BannerDraft>(() => emptyDraft());
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);

  const {
    data: config,
    error,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: PROVIDER_BANNER_QUERY_KEY,
    queryFn: getProviderAnnouncementBanner,
  });

  const updateMutation = useMutation({
    mutationFn: updateProviderAnnouncementBanner,
    onSuccess: ({ config: updated }) => {
      if (updated == null) {
        void queryClient.invalidateQueries({ queryKey: PROVIDER_BANNER_QUERY_KEY });
      } else {
        queryClient.setQueryData(PROVIDER_BANNER_QUERY_KEY, updated);
      }
      setPendingChange(null);
    },
    onError: () => setPendingChange(null),
  });

  const rules = config?.rules ?? [];
  const selectedRule =
    rules.find(
      (rule) =>
        selectedRuleId != null &&
        getProviderAnnouncementBannerRuleBaseId(rule.id) === getProviderAnnouncementBannerRuleBaseId(selectedRuleId)
    ) ?? pickDefaultRule(rules);

  return (
    <div className='max-w-4xl space-y-5'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h2 className='font-mediumbold text-xl text-neutral-900'>Provider Banner</h2>
          <div className='mt-1 text-sm text-neutral-500'>
            Announcement banner on the provider portal home page, served by the{' '}
            <span className='font-mono text-xs'>{PROVIDER_ANNOUNCEMENT_BANNER_CONFIG_ID}</span> Statsig config. Only
            provider types with <span className='font-mono text-xs'>globalAnnouncementBannerConfigEnabled</span> see it.
            Providers can dismiss it until its copy changes.
          </div>
        </div>
        <div className='flex items-center gap-3'>
          {config != null && (
            <a
              className='text-sm text-blue-700 hover:underline'
              href={config.statsigUrl}
              rel='noreferrer'
              target='_blank'
            >
              Open in Statsig
            </a>
          )}
          <button
            type='button'
            disabled={isFetching}
            onClick={() => void refetch()}
            className='h-9 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-700 shadow-sm hover:border-neutral-400 disabled:opacity-50'
          >
            Refresh
          </button>
        </div>
      </div>

      {updateMutation.error != null && (
        <ErrorBanner
          title='Could not update the banner'
          message={updateMutation.error instanceof Error ? updateMutation.error.message : 'Update failed'}
        />
      )}

      {isLoading && <LoadingCard message={'Loading provider banner config...'} />}

      {error != null && (
        <ErrorBanner
          message={error instanceof Error ? error.message : 'Failed to load provider banner config'}
          onRetry={refetch}
        />
      )}

      {config != null && selectedRule == null && (
        <div className='rounded-lg border border-neutral-200 bg-white px-4 py-6 text-sm text-neutral-600'>
          This config has no rules to update. Add a rule in Statsig first.
        </div>
      )}

      {config != null && selectedRule != null && (
        <>
          {rules.length > 1 && (
            <div className='flex flex-wrap items-center gap-2'>
              <span className='text-sm text-neutral-500'>Rule</span>
              {rules.map((rule) => (
                <SegmentButton
                  key={rule.id}
                  active={rule.id === selectedRule.id}
                  label={`${rule.name} (${formatEnvironments(rule)})`}
                  onClick={() => setSelectedRuleId(rule.id)}
                />
              ))}
            </div>
          )}

          <LiveStatus
            config={config}
            rule={selectedRule}
            canUpdate={canUpdate}
            onEdit={(value) => setDraft(toDraft(value))}
            onTurnOff={() => setPendingChange({ rule: selectedRule, banner: null })}
          />

          <BannerComposer
            draft={draft}
            canUpdate={canUpdate}
            onChange={setDraft}
            onSubmit={(banner) => setPendingChange({ rule: selectedRule, banner })}
          />
        </>
      )}

      {pendingChange != null && (
        <ConfirmBannerDialog
          rule={pendingChange.rule}
          banner={pendingChange.banner}
          isSaving={updateMutation.isPending}
          onCancel={() => setPendingChange(null)}
          onConfirm={() => {
            updateMutation.reset();
            updateMutation.mutate({ ruleId: pendingChange.rule.id, banner: pendingChange.banner });
          }}
        />
      )}
    </div>
  );
}
